import { useState, useCallback } from 'react';

interface ZipCodeResult {
  state: string;
  country: 'US' | 'Canada';
  city?: string;
}

interface ZippopotamResponse {
  'post code': string;
  country: string;
  'country abbreviation': string;
  places: Array<{
    'place name': string;
    state: string;
    'state abbreviation': string;
  }>;
}

export const useZipCodeLookup = () => {
  const [isLookingUp, setIsLookingUp] = useState(false);

  const lookupZipCode = useCallback(async (zipCode: string): Promise<ZipCodeResult | null> => {
    if (!zipCode.trim()) {
      return null;
    }

    setIsLookingUp(true);

    try {
      // Clean the input - remove spaces and convert to uppercase
      const cleanedCode = zipCode.trim().replace(/\s+/g, '').toUpperCase();

      // Detect format:
      // US ZIP: 5 digits (e.g., "94102")
      // Canadian Postal Code: 3 chars (FSA: "A0A") OR 6 chars (Full: "A0A0A0")
      const isCanadianFormat = /^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(cleanedCode);
      const isUSFormat = /^\d{5}$/.test(cleanedCode);

      let response: Response;
      let country: 'US' | 'Canada';

      if (isCanadianFormat) {
        // For Canadian postal codes, use only the first 3 characters (FSA - Forward Sortation Area)
        // API: https://api.zippopotam.us/ca/A0A
        // Works for both 3-char input (A0A) and 6-char input (A0A0A0)
        const fsaCode = cleanedCode.slice(0, 3);
        response = await fetch(`https://api.zippopotam.us/ca/${fsaCode}`);
        country = 'Canada';

        // If not found in Canada, try US as fallback
        if (!response.ok) {
          response = await fetch(`https://api.zippopotam.us/us/${cleanedCode}`);
          country = 'US';
        }
      } else if (isUSFormat) {
        // Try US first for US format
        response = await fetch(`https://api.zippopotam.us/us/${cleanedCode}`);
        country = 'US';

        // If not found in US, try Canada as fallback
        if (!response.ok) {
          response = await fetch(`https://api.zippopotam.us/ca/${cleanedCode}`);
          country = 'Canada';
        }
      } else {
        // Invalid format - try both anyway
        response = await fetch(`https://api.zippopotam.us/us/${cleanedCode}`);
        country = 'US';

        if (!response.ok) {
          response = await fetch(`https://api.zippopotam.us/ca/${cleanedCode}`);
          country = 'Canada';
        }
      }

      if (!response.ok) {
        return null;
      }

      const data: ZippopotamResponse = await response.json();

      if (data.places && data.places.length > 0) {
        const place = data.places[0];

        return {
          state: place['state abbreviation'],
          country,
          city: place['place name'],
        };
      }

      return null;
    } catch (error) {
      console.error('Error looking up zip code:', error);
      return null;
    } finally {
      setIsLookingUp(false);
    }
  }, []);

  return {
    lookupZipCode,
    isLookingUp,
  };
};
